import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircleIcon, SendIcon, UserIcon, MailIcon } from 'lucide-react';
import { LOCATIONS } from '../../lib/locations';
import { isDuplicate, submitVote, submitVoteToGoogleSheet } from '../../lib/store';
import { celebrate } from '../../lib/confetti';
import { LocationCard } from './LocationCard';
import { ThankYou } from './ThankYou';
import type { Location } from '../../lib/types';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
type Errors = {
  name?: string;
  email?: string;
  location?: string;
};
interface VoteFormProps {
  selectedLocationId?: string | null;
}
export function VoteForm({
  selectedLocationId
}: VoteFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [duplicate, setDuplicate] = useState(false);
  const [syncError, setSyncError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<Location | null>(null);
  useEffect(() => {
    if (!selectedLocationId) return;
    setSelectedId(selectedLocationId);
    setErrors(current => ({
      ...current,
      location: undefined
    }));
  }, [selectedLocationId]);
  function validate(): Errors {
    const next: Errors = {};
    if (!name.trim()) next.name = 'Please enter your full name.';
    if (!email.trim()) next.email = 'Please enter your email address.';else if (!EMAIL_RE.test(email.trim())) next.email = 'Please enter a valid email address.';
    if (!selectedId) next.location = 'Please choose one destination.';
    return next;
  }
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setDuplicate(false);
    setSyncError(false);
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const loc = LOCATIONS.find(l => l.id === selectedId)!;
    const input = {
      fullName: name,
      email,
      city: loc.city,
      province: loc.province
    };
    if (isDuplicate(input.fullName, input.email)) {
      setDuplicate(true);
      return;
    }
    setSubmitting(true);
    try {
      await submitVoteToGoogleSheet(input);
      const savedVote = submitVote(input);
      if (!savedVote.ok) {
        setDuplicate(true);
        return;
      }
      celebrate();
      setDone(loc);
    } catch {
      setSyncError(true);
    } finally {
      setSubmitting(false);
    }
  }
  if (done) return <ThankYou location={done} data-id="element-322" data-mp="21ai0dy4mxhmt" />;
  return <form onSubmit={handleSubmit} noValidate className="rounded-[2rem] border border-white/80 bg-[#fbfaf6]/90 p-6 shadow-2xl backdrop-blur sm:p-8" data-id="element-323" data-mp="269b6h5hle6cy">
      {/* Name */}
      <div className="mb-5" data-id="element-324" data-mp="3ho87zeuyqo17">
        <label htmlFor="fullName" className="mb-1.5 flex items-center gap-1.5 font-display font-600 text-slate-700" data-id="element-325" data-mp="3hozsnun0v1cp">
          <UserIcon className="h-4 w-4 text-lavender-500" data-id="element-326" data-mp="3loicso70r8eh" />
          Full Name <span className="text-coral-500" data-id="element-327" data-mp="1a2zph48p40ll">*</span>
        </label>
        <input id="fullName" name="fullName" type="text" required autoComplete="name" value={name} onChange={e => setName(e.target.value)} placeholder="Enter your full name" aria-invalid={!!errors.name} aria-describedby={errors.name ? 'name-error' : undefined} className="w-full rounded-2xl border-2 border-[#dde5d9] bg-white px-4 py-3 text-[#2d463b] placeholder:text-[#9aaa9f] focus:border-[#91a889] focus:outline-none" data-id="element-328" data-mp="1a4aut8hshget" />
        {errors.name && <FieldError id="name-error" data-id="element-329" data-mp="ibtvz6nhd15a">{errors.name}</FieldError>}
      </div>

      {/* Email */}
      <div className="mb-6" data-id="element-330" data-mp="de1ubkx1a0nw">
        <label htmlFor="email" className="mb-1.5 flex items-center gap-1.5 font-display font-600 text-slate-700" data-id="element-331" data-mp="2cpbbq7hn8tpr">
          <MailIcon className="h-4 w-4 text-sky-500" data-id="element-332" data-mp="27qi0klptxesr" />
          Email Address <span className="text-coral-500" data-id="element-333" data-mp="27o5lcgbghije">*</span>
        </label>
        <input id="email" name="email" type="email" required autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" aria-invalid={!!errors.email} aria-describedby={errors.email ? 'email-error' : undefined} className="w-full rounded-2xl border-2 border-[#dde5d9] bg-white px-4 py-3 text-[#2d463b] placeholder:text-[#9aaa9f] focus:border-[#91a889] focus:outline-none" data-id="element-334" data-mp="14gm7dazg8zo8" />
        {errors.email && <FieldError id="email-error" data-id="element-335" data-mp="cooj24sgo6ip">{errors.email}</FieldError>}
      </div>

      {/* Locations */}
      <fieldset className="mb-4" data-id="element-336" data-mp="goyrrp7n1b19">
        <legend className="mb-3 font-display text-lg font-600 text-slate-800" data-id="element-337" data-mp="gq021rgtxthx">
          Choose Your Preferred Location <span className="text-coral-500" data-id="element-338" data-mp="2s1zdjbvfli9">*</span>
        </legend>
        <div role="radiogroup" aria-label="Preferred location" className="grid gap-3" data-id="element-339" data-mp="7nqsb3tdy45l">
          {LOCATIONS.map(loc => <LocationCard key={loc.id} location={loc} selected={selectedId === loc.id} onSelect={id => {
          setSelectedId(id);
          setErrors(e => ({
            ...e,
            location: undefined
          }));
        }} data-id="element-340" data-mp="3kaqrjt1v4crn" />)}
        </div>
        {errors.location && <FieldError id="loc-error" data-id="element-341" data-mp="71md62kp5uut">{errors.location}</FieldError>}
      </fieldset>

      {/* Submission notices */}
      <AnimatePresence data-id="element-342" data-mp="23t7danb32v2">
        {syncError && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} role="alert" className="mb-4 flex items-center gap-2 rounded-2xl bg-coral-400/10 px-4 py-3 text-coral-500" data-id="element-343" data-mp="20yldeon99zj">
            <AlertCircleIcon className="h-5 w-5 shrink-0" data-id="element-344" data-mp="psvn2cydqlmx" />
            <span className="font-600" data-id="element-345" data-mp="lrtozzg78fmj">We couldn’t reach the voting sheet. Please check your connection and try again.</span>
          </motion.div>}
        {duplicate && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} role="alert" className="mb-4 flex items-center gap-2 rounded-2xl bg-coral-400/10 px-4 py-3 text-coral-500" data-id="element-346" data-mp="xss7i5i5poa6">
            <AlertCircleIcon className="h-5 w-5 shrink-0" data-id="element-347" data-mp="1bkq7nqtw49u7" />
            <span className="font-600" data-id="element-348" data-mp="1bkgiu6kl08q3">
              It looks like you've already voted. Thank you!
            </span>
          </motion.div>}
      </AnimatePresence>

      <motion.button type="submit" disabled={submitting} whileTap={{
      scale: 0.98
    }} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#c49a45] px-6 py-4 font-display text-xl font-600 text-[#29433b] shadow-lg transition hover:bg-[#dbba6c] disabled:cursor-not-allowed disabled:opacity-70" data-id="element-349" data-mp="1ljxnqls0eil0">
        <SendIcon className="h-5 w-5" data-id="element-350" data-mp="3sb6rywrqbtqa" />
        {submitting ? 'Casting…' : 'Cast My Vote'}
      </motion.button>
    </form>;
}
function FieldError({
  id,
  children
}: {
  id: string;
  children: React.ReactNode;
}) {
  return <p id={id} className="mt-1.5 flex items-center gap-1 text-sm font-600 text-coral-500" data-id="element-351" data-mp="20le7bjev8qcr">
      <AlertCircleIcon className="h-3.5 w-3.5" data-id="element-352" data-mp="1vn2zfe84spx6" />
      {children}
    </p>;
}